import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api, apiDownload, apiUpload } from '@/lib/api-client';
import { env } from '@/config/env';
import type {
  KnowledgeBody,
  KnowledgeDetail,
  KnowledgeFile,
  KnowledgeFolder,
  KnowledgeFolderBody,
} from '@erp/shared';

const KNOWLEDGE_KEY = ['erp', 'knowledge'] as const;

// ── Pastas ──────────────────────────────────────────────────────────────────

export function useCreateFolder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: KnowledgeFolderBody) => {
      const res = await api<KnowledgeFolder>('/knowledge/folders', { method: 'POST', body });
      if (res.error) throw res.error;
      return res.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: KNOWLEDGE_KEY }),
  });
}

export function useUpdateFolder(folderId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: KnowledgeFolderBody) => {
      const res = await api<KnowledgeFolder>(`/knowledge/folders/${folderId}`, {
        method: 'PUT',
        body,
      });
      if (res.error) throw res.error;
      return res.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: KNOWLEDGE_KEY }),
  });
}

export function useDeleteFolder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (folderId: number) => {
      const res = await api<void>(`/knowledge/folders/${folderId}`, { method: 'DELETE' });
      if (res.error) throw res.error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: KNOWLEDGE_KEY }),
  });
}

// ── Conhecimentos ───────────────────────────────────────────────────────────

export function useCreateKnowledge() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: KnowledgeBody) => {
      const res = await api<KnowledgeDetail>('/knowledge', { method: 'POST', body });
      if (res.error) throw res.error;
      return res.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: KNOWLEDGE_KEY }),
  });
}

export function useUpdateKnowledge(knowledgeId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: KnowledgeBody) => {
      const res = await api<KnowledgeDetail>(`/knowledge/${knowledgeId}`, { method: 'PUT', body });
      if (res.error) throw res.error;
      return res.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: KNOWLEDGE_KEY }),
  });
}

export function useDeleteKnowledge() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (knowledgeId: number) => {
      const res = await api<void>(`/knowledge/${knowledgeId}`, { method: 'DELETE' });
      if (res.error) throw res.error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: KNOWLEDGE_KEY }),
  });
}

export function useKnowledgeFeedback(knowledgeId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (helpful: boolean) => {
      const res = await api<void>(`/knowledge/${knowledgeId}/feedback`, {
        method: 'POST',
        body: { helpful },
      });
      if (res.error) throw res.error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: KNOWLEDGE_KEY }),
  });
}

// ── Anexos ──────────────────────────────────────────────────────────────────

/** Upload multipart (campo 'file') via helper compartilhado apiUpload. */
export function useUploadKnowledgeFile(knowledgeId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (file: File) => apiUpload<KnowledgeFile>(`/knowledge/${knowledgeId}/files`, file),
    onSuccess: () => qc.invalidateQueries({ queryKey: KNOWLEDGE_KEY }),
  });
}

export function useDeleteKnowledgeFile() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (fileId: number) => {
      const res = await api<void>(`/knowledge/files/${fileId}`, { method: 'DELETE' });
      if (res.error) throw res.error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: KNOWLEDGE_KEY }),
  });
}

/** Download autenticado (fetch + blob + <a download>) via helper apiDownload. */
export function downloadKnowledgeFile(file: KnowledgeFile): Promise<void> {
  return apiDownload(`/knowledge/files/${file.id}`, file.fileName);
}

// ── Feedback público (sem auth) ─────────────────────────────────────────────

export function usePublicKbFeedback(slug: string) {
  return useMutation({
    mutationFn: async (helpful: boolean) => {
      const url = new URL(
        `${env.apiBaseUrl}/api/v1/public/kb/${encodeURIComponent(slug)}/feedback`,
        window.location.origin
      );
      const res = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ helpful }),
      });
      if (!res.ok) throw { status: res.status, error: `http_${res.status}` };
    },
  });
}
